import { useEffect, useRef, forwardRef, useImperativeHandle, useState } from "react";
import { fetchKnitAuthSession } from "@/services/knitService";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// For TSX to recognize the knit-auth custom element
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'knit-auth': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}

interface KnitAuthProps {
  appId: string;
  triggerRef?: React.RefObject<HTMLButtonElement>;
  isAlreadyIntegrated?: boolean; // New prop to indicate if the app is already integrated
}

interface IntegrationError {
  code: string;
  description: string;
  resolution: string;
}

interface IntegrationDetails {
  integrationId: string;
  appId: string;
  categoryId: string;
  originOrgId: string;
  success: boolean;
  errors?: IntegrationError[];
}

export interface KnitAuthRef {
  openAuth: () => Promise<void>;
}

export const KnitAuth = forwardRef<KnitAuthRef, KnitAuthProps>(({ appId, triggerRef, isAlreadyIntegrated = false }, ref) => {
  const knitRef = useRef<HTMLElement>(null);
  const hiddenTriggerRef = useRef<HTMLButtonElement>(null);
  const mergedTriggerRef = triggerRef || hiddenTriggerRef;
  const [integrationResult, setIntegrationResult] = useState<{
    success?: boolean;
    error?: IntegrationError;
    message?: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [sessionInitialized, setSessionInitialized] = useState(false);

  // Backend call to generate & get the authSessionToken
  const newSessionFn = async (e?: CustomEvent) => {
    e?.preventDefault();
    
    try {
      // First check if this app is already integrated - if so, no need to open another session
      if (isAlreadyIntegrated) {
        // console.log("App is already integrated, skipping auth session");
        toast.info(`You already have an active integration with ${appId.toUpperCase()}`);
        return;
      }
      
      // Check if we've already initialized a session to prevent multiple popups
      if (sessionInitialized) {
        // console.log("Session already initialized, preventing duplicate");
        return;
      }
      
      // console.log("Initiating auth session for app:", appId);
      
      // Clear any existing auth token to start fresh
      if (knitRef.current) {
        knitRef.current.removeAttribute("authsessiontoken");
      }
      
      setLoading(true);
      setSessionInitialized(true);
      
      // Get auth session token for specific app
      try {
        // console.log("Fetching auth session token...");
        const token = await fetchKnitAuthSession(appId);
        // console.log("Token received:", token ? "✓" : "✗");
        
        if (!token) {
          throw new Error("Received empty token from authentication service");
        }
        
        // Set the token on the knit-auth element
        if (knitRef.current) {
          // UI Auth component won't open with an empty session token
          knitRef.current.setAttribute("authsessiontoken", token);
          // console.log("Token set on knit-auth element");
          
          // Force open the dialog programmatically
          setTimeout(() => {
            // console.log("Attempting to click trigger button");
            const triggerButton = mergedTriggerRef.current;
            if (triggerButton) {
              // Add the class to the body to prevent background scrolling
              document.body.classList.add('knit-auth-open');
              
              // Create and dispatch a native click event
              const clickEvent = new MouseEvent('click', {
                view: window,
                bubbles: true,
                cancelable: true
              });
              triggerButton.dispatchEvent(clickEvent);
              // console.log("Click event dispatched to trigger button");
            } else {
              // console.error("No trigger button found to open Knit Auth dialog");
              toast.error("Failed to open authentication dialog");
            }
          }, 200); // Increased timeout for better reliability
        } else {
          // console.error("knit-auth element reference not found");
          toast.error("Authentication component not initialized properly");
        }
      } catch (tokenError: any) {
        // console.error("Error obtaining auth token:", tokenError);
        toast.error(`Authentication error: ${tokenError.message || "Failed to get authentication token"}`);
        // Reset session initialized flag to allow retry
        setSessionInitialized(false);
        setIntegrationResult({
          success: false,
          message: `Failed to get authentication token: ${tokenError.message || "Unknown error"}`
        });
      }
    } catch (err: any) {
      // console.error("Error in authentication flow:", err);
      toast.error("Failed to initialize integration");
      setIntegrationResult({
        success: false,
        message: `Failed to initialize integration: ${err.message || "Unknown error"}`
      });
      // Reset session initialized flag to allow retry
      setSessionInitialized(false);
    } finally {
      setLoading(false);
    }
  };

  // Expose the openAuth method to parent components
  useImperativeHandle(ref, () => ({
    openAuth: newSessionFn
  }));

  // Save integration to database if successful
  const saveIntegration = async (details: IntegrationDetails) => {
    try {
      // console.log("Saving integration to database:", details);
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // console.error("No authenticated session found");
        throw new Error("No authenticated session found");
      }
      
      // console.log("Making API call to save integration...");
      // Use the new knit-helper function endpoint
      const response = await fetch('https://jftsemrnjqbzinphyrpm.supabase.co/functions/v1/knit-helper/save-integration', {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ 
          integrationDetails: details 
        })
      });
      
      // console.log("Save integration API response status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        // console.error("Error saving integration (status code):", response.status, errorText);
        throw new Error(`Error saving integration: ${response.statusText} (${errorText.substring(0, 100)})`);
      }
      
      const data = await response.json();
      // console.log("Save integration API response:", data);
      
      if (data.success) {
        // console.log("Integration saved successfully:", data);
        // Reset the session initialized flag after successful save
        setSessionInitialized(false);
        return true;
      } else {
        // console.error("Failed to save integration:", data);
        return false;
      }
    } catch (error) {
      // console.error("Error saving integration:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Upon finishing the integration flow
  const onFinishFn = async (e: CustomEvent) => {
    e?.preventDefault();
    // console.log("Integration flow finished, event detail:", e.detail);
    const details = e["detail"]["integrationDetails"] as IntegrationDetails;
    // console.log("Integration details:", details);
    
    if (details.success) {
      // console.log("Integration successful, attempting to save to database...");
      // Save successful integration to database
      const saved = await saveIntegration(details);
      
      if (saved) {
        // console.log("Integration saved successfully, dispatching success event");
        setIntegrationResult({
          success: true,
          message: `Successfully integrated with ${details.appId.toUpperCase()}`
        });
        toast.success(`Successfully integrated with ${details.appId.toUpperCase()}`);
        
        // Dispatch a custom event that can be listened for by parent components
        // console.log("Dispatching knit:integration-success event");
        window.dispatchEvent(new CustomEvent('knit:integration-success', {
          detail: {
            appId: details.appId,
            integrationId: details.integrationId
          }
        }));
        // console.log("Event dispatched");
      } else {
        // console.error("Integration completed, but failed to save details");
        setIntegrationResult({
          success: true,
          message: "Integration completed, but failed to save details"
        });
        toast.warning("Integration completed, but failed to save details");
      }
    } else if (details.errors && details.errors.length > 0) {
      // Show error message for failed integration
      const error = details.errors[0];
      // console.error("Integration failed with errors:", details.errors);
      setIntegrationResult({
        success: false,
        error
      });
      toast.error(`Integration failed: ${error.description}`);
      // Reset session initialized flag to allow retry
      setSessionInitialized(false);
    } else {
      // Generic error if no specific errors provided
      // console.error("Integration failed with unknown error");
      setIntegrationResult({
        success: false,
        message: "Integration failed with unknown error"
      });
      toast.error("Integration failed with unknown error");
      // Reset session initialized flag to allow retry
      setSessionInitialized(false);
    }
  };

  // Upon deactivate of integration
  const onDeactivateFn = (e: CustomEvent) => {
    e?.preventDefault();
    // console.log("Integration deactivated:", e["detail"]["integrationDetails"]);
    // Reset session initialized flag
    setSessionInitialized(false);
  };

  // Upon closing of the knit-auth component
  const onKnitCloseFn = (e: CustomEvent) => {
    e?.preventDefault();
    // console.log("Knit component closed:", e["detail"]["knitClosed"]);
    // Reset session initialized flag when the component is closed
    setSessionInitialized(false);
    
    // Clear the auth session token
    if (knitRef.current) {
      knitRef.current.removeAttribute("authsessiontoken");
    }
    
    // Remove the body class to re-enable scrolling
    document.body.classList.remove('knit-auth-open');
  };

  // Add a function to reset the session state
  const resetSession = () => {
    // console.log("Resetting Knit auth session state");
    setSessionInitialized(false);
    if (knitRef.current) {
      knitRef.current.removeAttribute("authsessiontoken");
    }
  };

  // Update document click handler
  useEffect(() => {
    // Wait for DOM to be ready
    setTimeout(() => {
      // Assign functions to event listeners for onNewSession and onFinish events.
      if (knitRef.current) {
        knitRef.current.addEventListener("onNewSession", newSessionFn as unknown as EventListener);
        knitRef.current.addEventListener("onFinish", onFinishFn as unknown as EventListener);
        knitRef.current.addEventListener("onDeactivate", onDeactivateFn as EventListener);
        knitRef.current.addEventListener("onKnitClose", onKnitCloseFn as EventListener);
        // console.log("Event listeners attached to knit-auth element");
      } else {
        // console.error("Could not attach event listeners - knit-auth element not found");
      }
    }, 100);
    
    // Reset integration result and sessionInitialized state when appId changes
    setIntegrationResult(null);
    resetSession();
    
    // Add document click handler to reset sessionInitialized state if the auth dialog wasn't opened
    const handleDocumentClick = () => {
      setTimeout(() => {
        // If we've initialized a session but there's no active auth dialog visible, 
        // we should reset the state to allow another attempt
        if (sessionInitialized && knitRef.current) {
          const knitAuthElement = knitRef.current as HTMLElement;
          // Check if the Knit auth dialog is actually visible
          const isDialogOpen = knitAuthElement.getAttribute('open') === 'true' || 
                          knitAuthElement.classList.contains('knit-auth-open');
          
          if (!isDialogOpen) {
            // console.log("Detected no active Knit dialog despite initialized session, resetting state");
            resetSession();
          }
        }
      }, 300);  // Give it a short delay to let the dialog open if it's going to
    };
    
    document.addEventListener('click', handleDocumentClick);
    
    // Cleanup on unmount
    return () => {
      if (knitRef.current) {
        knitRef.current.removeEventListener("onNewSession", newSessionFn as unknown as EventListener);
        knitRef.current.removeEventListener("onFinish", onFinishFn as unknown as EventListener);
        knitRef.current.removeEventListener("onDeactivate", onDeactivateFn as EventListener);
        knitRef.current.removeEventListener("onKnitClose", onKnitCloseFn as EventListener);
      }
      document.removeEventListener('click', handleDocumentClick);
      
      // Reset the sessionInitialized state when unmounting
      resetSession();
    };
  }, [appId]);

  // Add a useEffect to inject custom styles for the knit-auth dialog positioning
  useEffect(() => {
    // Create a style element
    const styleElement = document.createElement('style');
    styleElement.id = 'knit-auth-custom-styles';
    
    // Add the CSS to fix overlay and dialog positioning
    styleElement.textContent = `
      /* Fix for oversized overlay */
      knit-auth::part(overlay),
      knit-auth::shadow(overlay),
      knit-auth #knit-auth-overlay,
      .knit-auth-overlay {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        background-color: rgba(0, 0, 0, 0.5) !important;
        z-index: 9999 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        overflow: hidden !important;
      }

      /* Fix for popup positioning */
      knit-auth::part(dialog),
      knit-auth::shadow(dialog),
      knit-auth #knit-auth-dialog,
      knit-auth .knit-auth-dialog,
      .knit-auth-dialog {
        position: fixed !important;
        top: 50% !important;
        left: 50% !important;
        transform: translate(-50%, -50%) !important;
        max-height: 90vh !important;
        max-width: 90vw !important;
        width: auto !important;
        height: auto !important;
        overflow: auto !important;
        margin: 0 !important;
        z-index: 10000 !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1) !important;
        background: white !important;
      }
      
      /* Ensure the iframe is properly sized */
      knit-auth iframe {
        max-height: 80vh !important;
        max-width: 100% !important;
        border-radius: 8px !important;
        border: none !important;
      }
      
      /* Add scrolling to the content if needed */
      .knit-auth-content,
      knit-auth-content,
      knit-auth .content {
        overflow-y: auto !important;
        max-height: calc(90vh - 60px) !important;
        padding: 20px !important;
      }
      
      /* Fix any potential body scrolling issues when dialog is open */
      body.knit-auth-open {
        overflow: hidden !important;
      }
    `;
    
    // Add the style element to the head
    document.head.appendChild(styleElement);
    
    // Add a class to the body when the dialog is opened
    const handleDialogOpen = () => {
      document.body.classList.add('knit-auth-open');
    };
    
    // Remove the class when dialog is closed
    const handleDialogClose = () => {
      document.body.classList.remove('knit-auth-open');
    };
    
    // Try to add event listeners if the knit-auth element exposes them
    if (knitRef.current) {
      knitRef.current.addEventListener('onOpen', handleDialogOpen as EventListener);
      knitRef.current.addEventListener('onClose', handleDialogClose as EventListener);
    }
    
    // Clean up on component unmount
    return () => {
      const existingStyle = document.getElementById('knit-auth-custom-styles');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
      
      // Remove body class
      document.body.classList.remove('knit-auth-open');
      
      // Remove event listeners
      if (knitRef.current) {
        knitRef.current.removeEventListener('onOpen', handleDialogOpen as EventListener);
        knitRef.current.removeEventListener('onClose', handleDialogClose as EventListener);
      }
    };
  }, []);

  useEffect(() => {
    // console.log("Setting up knit-auth element event listeners");
    
    // Load the knit-auth element script
    const scriptId = "knit-auth-element-script";
    if (!document.getElementById(scriptId)) {
      // console.log("Loading knit-auth web component script");
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = "https://knit-labs.github.io/knit-auth-element/v2/knit-auth.min.js";
      script.async = true;
      script.onload = () => {
        // console.log("knit-auth script loaded successfully");
      };
      script.onerror = (e) => {
        // console.error("Error loading knit-auth script:", e);
      };
      document.body.appendChild(script);
    }
    
    // Use properly typed handler functions for type safety
    const handleFinish = (e: Event) => {
      // console.log("knit-auth:finish event received via addEventListener", e);
      onFinishFn(e as CustomEvent);
    };
    
    const handleDeactivate = (e: Event) => {
      // console.log("knit-auth:deactivate event received via addEventListener", e);
      onDeactivateFn(e as CustomEvent);
    };
    
    // Add event listeners to the current knit-auth element
    const knitAuthElement = knitRef.current;
    if (knitAuthElement) {
      // console.log("Adding event listeners to knit-auth element");
      
      // Some browsers use addEventListener, others use .on
      try {
        if (typeof knitAuthElement.addEventListener === 'function') {
          knitAuthElement.addEventListener("knit-auth:finish", handleFinish);
          knitAuthElement.addEventListener("knit-auth:deactivate", handleDeactivate);
          
          // console.log("Added event listeners via addEventListener");
        } else {
          // console.log("addEventListener not available, trying 'on' property");
          // Using .on property syntax (for older or non-standard web components)
          // @ts-ignore - this is a custom element property
          knitAuthElement.on = {
            'knit-auth:finish': (e: CustomEvent) => {
              // console.log("knit-auth:finish event received via .on property", e);
              onFinishFn(e);
            },
            'knit-auth:deactivate': (e: CustomEvent) => {
              // console.log("knit-auth:deactivate event received via .on property", e);
              onDeactivateFn(e);
            }
          };
          
          // console.log("Added event handlers via .on property");
        }
      } catch (error) {
        // console.error("Error setting up event listeners:", error);
      }
    } else {
      // console.warn("knit-auth element not found during initial setup");
    }
    
    return () => {
      // Cleanup event listeners
      const element = knitRef.current;
      if (element && typeof element.removeEventListener === 'function') {
        // console.log("Removing event listeners from knit-auth element");
        // Use the same function references for removal
        element.removeEventListener("knit-auth:finish", handleFinish);
        element.removeEventListener("knit-auth:deactivate", handleDeactivate);
      }
    };
  }, []);

  return (
    <div className="space-y-4">
      {integrationResult && (
        <Alert variant={integrationResult.success ? "default" : "destructive"} className="mt-4">
          {integrationResult.success ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <AlertTitle>
            {integrationResult.success ? "Integration successful" : "Integration failed"}
          </AlertTitle>
          <AlertDescription>
            {integrationResult.message || (
              integrationResult.error ? (
                <div className="space-y-2">
                  <p>{integrationResult.error.description}</p>
                  <p className="text-sm font-medium">Resolution: {integrationResult.error.resolution}</p>
                </div>
              ) : "Unknown error occurred"
            )}
          </AlertDescription>
        </Alert>
      )}
      
      <knit-auth ref={knitRef}>
        <button 
          slot="trigger" 
          ref={mergedTriggerRef}
          style={{ 
            position: 'absolute',
            opacity: 0, // Hide the button
            pointerEvents: 'none', // Prevent direct clicks
            height: '1px',
            width: '1px',
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            margin: '-1px',
            padding: 0,
            border: 0
          }}
          disabled={loading || isAlreadyIntegrated}
        >
          Trigger Knit Auth
        </button>
      </knit-auth>
    </div>
  );
});

export default KnitAuth;